import { Check } from 'lucide-react'
import type { ThemeType } from '@/types'
import { THEME_METADATA } from '@/themes/themeConfig'
import { useTranslation } from 'react-i18next'

interface ThemeSelectorProps {
  currentTheme: ThemeType
  onChange: (theme: ThemeType) => void
}

// Visual preview components for each theme
const ThemePreview = ({ theme }: { theme: ThemeType }) => {
  const previews = {
    modern: (
      <div className="w-full h-32 rounded-lg overflow-hidden relative bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="absolute inset-4 bg-white/40 backdrop-blur-xl rounded-3xl border border-white/60 shadow-lg p-3 space-y-2">
          <div className="h-2 w-16 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
          <div className="h-1.5 w-12 bg-gray-300/60 rounded-full"></div>
          <div className="h-1.5 w-20 bg-gray-300/60 rounded-full"></div>
        </div>
        <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 blur-xl opacity-60"></div>
        <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 blur-lg opacity-60"></div>
      </div>
    ),
    classic: (
      <div className="w-full h-32 rounded-lg overflow-hidden bg-white border border-gray-300 p-4 space-y-2">
        <div className="border-b-2 border-gray-800 pb-2">
          <div className="h-2 w-20 bg-gray-800 mb-1"></div>
          <div className="h-1 w-16 bg-gray-500"></div>
        </div>
        <div className="space-y-1.5">
          <div className="h-1 w-full bg-gray-300"></div>
          <div className="h-1 w-full bg-gray-300"></div>
          <div className="h-1 w-3/4 bg-gray-300"></div>
        </div>
        <div className="h-1 w-24 bg-gray-700 mt-2"></div>
      </div>
    ),
    minimal: (
      <div className="w-full h-32 rounded-lg overflow-hidden bg-white p-4 flex flex-col justify-center space-y-4">
        <div className="space-y-1.5">
          <div className="h-3 w-28 bg-gray-900"></div>
          <div className="h-0.5 w-20 bg-gray-300"></div>
        </div>
        <div className="space-y-1">
          <div className="h-0.5 w-full bg-gray-200"></div>
          <div className="h-0.5 w-5/6 bg-gray-200"></div>
        </div>
      </div>
    ),
    creative: (
      <div className="w-full h-32 rounded-lg overflow-hidden relative bg-gradient-to-br from-yellow-100 via-pink-100 to-purple-100 border-4 border-black p-3">
        <div className="absolute top-2 -right-1 bg-yellow-400 border-2 border-black px-2 py-0.5 -rotate-12">
          <div className="h-1 w-12 bg-black"></div>
        </div>
        <div className="space-y-2 mt-4">
          <div className="h-2.5 w-24 bg-black"></div>
          <div className="h-1 w-20 bg-gray-700"></div>
          <div className="h-6 w-6 bg-gradient-to-br from-pink-500 to-purple-500 border-2 border-black rotate-45 mt-2"></div>
        </div>
        <div className="absolute bottom-2 right-2 h-1 w-16 bg-black -rotate-6"></div>
      </div>
    ),
  }

  return previews[theme]
}

export default function ThemeSelector({
  currentTheme,
  onChange,
}: ThemeSelectorProps) {
  const { t } = useTranslation('profile')
  const themes: ThemeType[] = ['modern', 'classic', 'minimal', 'creative']

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('sharing.themeSelector.title')}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {t('sharing.themeSelector.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {themes.map((theme) => {
          const metadata = THEME_METADATA[theme]
          const isSelected = currentTheme === theme

          return (
            <button
              key={theme}
              onClick={() => onChange(theme)}
              className={`
                relative p-4 rounded-lg border-2 text-left transition-all group
                ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                }
              `}
            >
              {isSelected && (
                <div className="absolute top-3 right-3 z-10">
                  <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}

              {/* Visual Preview Thumbnail */}
              <div className="mb-4">
                <ThemePreview theme={theme} />
              </div>

              <h4 className="font-semibold text-gray-900 mb-1">
                {metadata.name}
              </h4>
              <p className="text-sm text-gray-600 mb-3">{metadata.description}</p>

              {/* Features list */}
              {'features' in metadata && metadata.features && (
                <ul className="text-xs text-gray-500 space-y-1">
                  {metadata.features.slice(0, 2).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-1">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
